import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import LastUpdatedChip from "@/components/LastUpdatedChip";
import useAutoRefresh from "@/lib/useAutoRefresh";
import Sparkline from "@/components/Sparkline";
import { toast } from "sonner";
import { toastPaymentReceived } from "@/components/AnimatedToasts";
import { playDing } from "@/lib/audio";
import {
    UploadSimple, HourglassMedium, CheckCircle, XCircle, Receipt,
    CalendarBlank, Bell, ArrowRight,
} from "@phosphor-icons/react";
import { isTriColorSeason } from "@/components/TriColorRibbon";
import SparklerFireworks, { isNationalDayToday } from "@/components/SparklerFireworks";
import FestivalBanner from "@/components/FestivalBanner";

const fmt = (n) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

/**
 * v1.27.67 — Compact Home for the 4-tab bottom-nav layout.
 *
 * Structure (matches modern fintech home cards):
 *   1. Compact greeting header
 *   2. Big balance hero card (teal gradient — keeps existing brand colours)
 *   3. Quick-action circle row (Upload / Ledger / Reports / Notifications)
 *   4. Latest activity chip (one line, deep-links into Uploads tab)
 *   5. Last-updated + refresh chip
 *
 * Recent Uploads table has moved to the Uploads tab.
 */
export default function CustomerDashboard() {
    const { data, lastUpdated, refresh, isRefreshing } = useAutoRefresh(
        () => api.get("/dashboard/customer").then((r) => r.data),
        { intervalMs: 30_000 },
    );
    const [history, setHistory] = useState([]);
    useEffect(() => {
        api.get("/dashboard/customer/balance-history?days=30")
            .then((r) => setHistory((r.data?.points || []).map((p) => p.balance)))
            .catch(() => setHistory([]));
    }, [data?.balance]);

    // Pulse-toast when a recent upload flips status between refreshes.
    const prevStatusesRef = useRef(null);
    useEffect(() => {
        if (!data?.recent_uploads) return;
        const nextMap = new Map(data.recent_uploads.map((u) => [u.id, u]));
        const prev = prevStatusesRef.current;
        if (prev === null) { prevStatusesRef.current = nextMap; return; }
        for (const [id, u] of nextMap) {
            const before = prev.get(id);
            if (!before) continue;
            if (before.status !== u.status) {
                if (u.status === "verified") {
                    toastPaymentReceived(fmt(u.amount));
                    playDing();
                } else if (u.status === "rejected") {
                    toast.error(`Payment rejected · ${fmt(u.amount)}`, {
                        description: u.utr ? `UTR ${u.utr} — contact admin` : "Contact admin",
                        icon: <XCircle size={18} weight="fill" className="text-rose-600" />,
                    });
                }
            }
        }
        prevStatusesRef.current = nextMap;
    }, [data]);

    const [fireworks, setFireworks] = useState(false);
    useEffect(() => {
        if (!isNationalDayToday()) return;
        const initial = setTimeout(() => setFireworks(true), 800);
        return () => clearTimeout(initial);
    }, []);

    if (!data) return <div className="text-slate-500">Loading…</div>;

    const flagSeason = isTriColorSeason();
    const latest = (data.recent_uploads || [])[0];
    const bal = Number(data.balance || 0);
    const balTone = bal > 0 ? "owe" : bal < 0 ? "due" : "settled";
    const pendingAmt = Number(data.pending_amount || 0);
    const combinedTotal = Math.abs(bal) + pendingAmt;
    const companyLabel = data.company_name || "us";
    const showTrend = history.length > 1 && history.some((v) => v !== 0);

    const greetingHour = new Date().getHours();
    const greeting = greetingHour < 12 ? "Good morning" : greetingHour < 17 ? "Good afternoon" : "Good evening";

    return (
        <div className="space-y-5" data-testid="customer-dashboard">
            <SparklerFireworks open={fireworks} onClose={() => setFireworks(false)} />
            {/* v1.27.95 — Auto Festival Banner (portal side). Same 4-days-before
                → 1-day-after window; adds a warm greeting card above the
                party greeting without touching any balance / txn data. */}
            <FestivalBanner />

            {/* Compact greeting */}
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-xs text-slate-500">{greeting}</div>
                    <h1 data-testid="customer-welcome-name" className="mt-0.5 font-display text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
                        {data.party?.name}
                    </h1>
                </div>
                {flagSeason && (
                    <button
                        type="button"
                        data-testid="customer-fire-sparklers-btn"
                        onClick={() => setFireworks(true)}
                        title="Independence day fireworks"
                        className="flex h-9 items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-3 text-amber-900 transition-transform hover:scale-105 hover:bg-amber-100"
                    >
                        <span className="text-sm leading-none">🎆</span>
                        <span className="text-[10px] font-bold uppercase tracking-widest">Fire!</span>
                    </button>
                )}
            </div>

            {/* Balance hero card — teal gradient, mirrors the reference card layout */}
            <div
                data-testid="customer-balance-card"
                className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0F544D] via-[#0F544D] to-[#0B3D38] p-5 text-white shadow-lg ring-1 ring-teal-900/40"
            >
                <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-teal-400/10 blur-2xl" />
                <div className="absolute -bottom-10 -left-6 h-28 w-28 rounded-full bg-amber-300/10 blur-2xl" />
                <div className="relative flex items-center gap-3">
                    <img src="/logo512.png" alt="MD Impex India" data-testid="dashboard-logo" className="h-9 w-9 shrink-0 rounded-md object-cover ring-1 ring-white/40" />
                    <div className="min-w-0">
                        <div
                            className="brand-gold brand-gold-shimmer truncate"
                            data-testid="dashboard-company-name"
                            style={{ fontSize: "13px", letterSpacing: "0.18em", lineHeight: 1 }}
                        >
                            {data.company_name || "MD Impex India"}
                        </div>
                        <div className="mt-1 truncate text-xs text-white/80">Collection Portal · v27</div>
                    </div>
                </div>
                <div className="relative mt-5 space-y-4">
                    {/* 1. Amount Payable — net ledger balance payable to/from the party */}
                    <div data-testid="figure-amount-payable">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/75">
                            Amount Payable
                        </div>
                        <div className="mt-1 font-display text-4xl font-black tabular-nums text-white drop-shadow-sm sm:text-5xl" data-testid="amount-payable-value">
                            {fmt(Math.abs(bal))}
                        </div>
                        <div className="mt-1 text-[11px] text-white/70">
                            {balTone === "owe"
                                ? `Payable to ${companyLabel}`
                                : balTone === "due"
                                ? `${companyLabel} owe you`
                                : "All settled · nothing payable"}
                        </div>
                    </div>

                    {/* 2 + 3. Pending for verification + Combined total */}
                    <div className="grid grid-cols-2 gap-3 border-t border-white/15 pt-3">
                        <div data-testid="figure-amount-pending">
                            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/70">
                                Pending for Verification
                            </div>
                            <div className="mt-1 font-display text-xl font-bold tabular-nums text-amber-200 sm:text-2xl" data-testid="amount-pending-value">
                                {fmt(pendingAmt)}
                            </div>
                            <div className="mt-0.5 text-[10px] text-white/60">
                                {data.pending_uploads} slip{data.pending_uploads === 1 ? "" : "s"} awaiting review
                            </div>
                        </div>
                        <div data-testid="figure-combined-total">
                            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/70">
                                Combined Total
                            </div>
                            <div className="mt-1 font-display text-xl font-bold tabular-nums text-white sm:text-2xl" data-testid="amount-combined-value">
                                {fmt(combinedTotal)}
                            </div>
                            <div className="mt-0.5 text-[10px] text-white/60">
                                Payable + pending
                            </div>
                        </div>
                    </div>
                </div>
                {showTrend && (
                    <div className="relative mt-4 flex items-center gap-2">
                        <Sparkline
                            data={history}
                            width={220}
                            height={26}
                            colorTop={balTone === "owe" ? "#fca5a5" : "#a7f3d0"}
                            testId="stat-trend-sparkline"
                        />
                        <span className="text-[9px] font-semibold uppercase tracking-widest text-white/60">30d</span>
                    </div>
                )}
            </div>

            {/* Settled banner */}
            {balTone === "settled" && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-xs text-emerald-800" data-testid="settled-banner">
                    ✓ You've cleared all outstanding dues. Nothing to pay right now.
                </div>
            )}

            {/* v1.27.68 — Upload activity widget: Yesterday vs Today. Reflects
                every slip uploaded by this party in the last two IST days.
                v1.27.69 — Also shows per-day approved / rejected split.
                Refreshes with the rest of the dashboard payload every 30 s. */}
            <div className="grid grid-cols-2 gap-3" data-testid="upload-activity-widget">
                <ActivityCard
                    label="Yesterday"
                    count={data.upload_activity?.yesterday?.count || 0}
                    amount={data.upload_activity?.yesterday?.amount || 0}
                    verified={data.upload_activity?.yesterday?.verified || 0}
                    rejected={data.upload_activity?.yesterday?.rejected || 0}
                    pending={data.upload_activity?.yesterday?.pending || 0}
                    tone="slate"
                    testId="upload-activity-yesterday"
                />
                <ActivityCard
                    label="Today"
                    count={data.upload_activity?.today?.count || 0}
                    amount={data.upload_activity?.today?.amount || 0}
                    verified={data.upload_activity?.today?.verified || 0}
                    rejected={data.upload_activity?.today?.rejected || 0}
                    pending={data.upload_activity?.today?.pending || 0}
                    tone="teal"
                    testId="upload-activity-today"
                />
            </div>

            {/* Quick actions — circular icon buttons like a modern fintech home */}
            <div className="grid grid-cols-4 gap-1 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm" data-testid="dashboard-quick-actions">
                <QuickAction to="/portal/upload"     icon={UploadSimple}   label="Upload"    testId="qa-upload" />
                <QuickAction to="/portal/reports"    icon={Receipt}        label="Ledger"    testId="qa-ledger" />
                <QuickAction to="/portal/reports"    icon={CalendarBlank}  label="Monthly"   testId="qa-monthly" />
                <QuickAction to="/portal/notifications" icon={Bell}        label="Alerts"    testId="qa-alerts" />
            </div>

            {/* Latest activity summary → deep links into Uploads tab */}
            {latest ? (
                <Link
                    to="/portal/uploads"
                    data-testid="dashboard-recent-summary"
                    className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                            latest.status === "verified" ? "bg-emerald-100 text-emerald-700"
                            : latest.status === "rejected" ? "bg-rose-100 text-rose-700"
                            : "bg-amber-100 text-amber-700"
                        }`}>
                            {latest.status === "verified"
                                ? <CheckCircle size={20} weight="fill" />
                                : latest.status === "rejected"
                                ? <XCircle size={20} weight="fill" />
                                : <HourglassMedium size={20} weight="fill" />}
                        </div>
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Latest upload</div>
                            <div className="mt-0.5 truncate text-sm font-semibold text-slate-800">
                                {fmt(latest.amount)}
                                <span className="ml-2 text-xs font-normal text-slate-500">
                                    · {String(latest.status || "").toUpperCase()}
                                </span>
                            </div>
                        </div>
                    </div>
                    <ArrowRight size={18} weight="bold" className="shrink-0 text-slate-400" />
                </Link>
            ) : (
                <Link
                    to="/portal/upload"
                    data-testid="dashboard-first-upload-cta"
                    className="block rounded-2xl border-2 border-dashed border-slate-300 bg-white px-4 py-5 text-center shadow-sm transition-colors hover:border-blue-400 hover:bg-blue-50/40"
                >
                    <UploadSimple size={26} weight="duotone" className="mx-auto text-blue-500" />
                    <div className="mt-1 text-sm font-semibold text-slate-800">Upload your first payment slip</div>
                    <div className="text-[11px] text-slate-500">We'll verify it and update your ledger automatically.</div>
                </Link>
            )}

            <div className="flex justify-end">
                <LastUpdatedChip lastUpdated={lastUpdated} isRefreshing={isRefreshing} onRefresh={refresh} testId="dashboard-last-updated" />
            </div>

        </div>
    );
}

function ActivityCard({ label, count, amount, verified, rejected, pending, tone, testId }) {
    // Tone-driven accents that stay inside the existing brand palette.
    const cls = tone === "teal"
        ? {
            wrap: "border-teal-200 bg-teal-50/70",
            label: "text-teal-700",
            count: "text-teal-900",
            amount: "text-teal-800",
            chip: "bg-teal-600 text-white",
        }
        : {
            wrap: "border-slate-200 bg-white",
            label: "text-slate-500",
            count: "text-slate-900",
            amount: "text-slate-700",
            chip: "bg-slate-800 text-white",
        };
    return (
        <div className={`rounded-2xl border ${cls.wrap} p-4 shadow-sm`} data-testid={testId}>
            <div className="flex items-center justify-between">
                <div className={`text-[10px] font-semibold uppercase tracking-[0.22em] ${cls.label}`}>{label}</div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${cls.chip}`} data-testid={`${testId}-count-chip`}>
                    {count} slip{count === 1 ? "" : "s"}
                </span>
            </div>
            <div className={`mt-2 font-display text-2xl font-bold tabular-nums ${cls.count}`} data-testid={`${testId}-count`}>
                {count}
            </div>
            <div className={`mt-0.5 text-xs font-semibold tabular-nums ${cls.amount}`} data-testid={`${testId}-amount`}>
                {fmt(amount)}
            </div>
            {/* v1.27.69 — Per-day approved / rejected / pending split so
                the party can see the ratio without opening the Uploads tab. */}
            <div className="mt-2 flex flex-wrap items-center gap-1 text-[10px] font-semibold" data-testid={`${testId}-breakdown`}>
                <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-800" data-testid={`${testId}-approved`}>
                    ✓ {verified || 0} approved
                </span>
                <span className="inline-flex items-center rounded-full bg-rose-100 px-2 py-0.5 text-rose-800" data-testid={`${testId}-rejected`}>
                    ✕ {rejected || 0} rejected
                </span>
                {(pending || 0) > 0 && (
                    <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-amber-800" data-testid={`${testId}-pending`}>
                        ⋯ {pending} pending
                    </span>
                )}
            </div>
        </div>
    );
}

function QuickAction({ to, icon: Icon, label, testId }) {
    return (
        <Link
            to={to}
            data-testid={testId}
            className="group flex flex-col items-center gap-1.5 rounded-xl px-2 py-2 transition-colors hover:bg-slate-50 active:bg-slate-100"
        >
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-900 text-white shadow-sm ring-2 ring-white transition-transform group-hover:-translate-y-0.5 group-active:scale-95">
                <Icon size={22} weight="duotone" />
            </div>
            <span className="text-[11px] font-semibold text-slate-700">{label}</span>
        </Link>
    );
}
